import React, { useState, useEffect } from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import IconButton from '@material-ui/core/IconButton'
import { Grid, StepContent } from '@material-ui/core'
import Stepper from '@material-ui/core/Stepper'
import Step from '@material-ui/core/Step'
import StepLabel from '@material-ui/core/StepLabel'
import StepConnector from '@material-ui/core/StepConnector'
import Divider from '@material-ui/core/Divider'
import CopyEntry from './CopyEntry'
import UploadTemplate from '@src/routes/Configurators/components/UploadTemplate'
import Button from '@material-ui/core/Button'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '../../../../messages'
import {
  useCopyPasteTubeMutation,
  useSessionFieldsMutation,
  useUploadTubesFromFileMutation,
} from '@src/mutations/ConfiguratorTubesMutation.generated'
import ValidationErrors from '../../../../validations/ValidationErrors'
import { biTubeConfigContext } from '../../BiTube'
import { fastDuplexOligosRoute, useRouter } from '@src/routes'
import {
  ActiveLinkType,
  FileFormat,
  BiTubeProduct,
  UploadType,
} from '../../enum'
import { EMPTY_SESSION } from '../../EmptySession'
import clsx from 'clsx'
import CircleLoader from '@src/components/CircleLoader/CircleLoader'
import {
  convertToASCII,
  checkMultipleLine,
} from '@src/routes/Configurators/utils/util'
import CautionPopUp from '@src/routes/Configurators/components/CautionPopup'
import { ConfiguratorValues } from '@src/routes/Configurators/utils/constants'
import { EventActions } from '@utils/analytics/types'
import { sendConfiguratorGADownloadEvent } from '@utils/analytics/configuratorAnalytics'

const useStyles = makeStyles((theme: Theme) => {
  return {
    contentWrapper: {
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(8, 5),
    },
    step: {
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(6),
    },
    mTop12: {
      marginTop: theme.spacing(-3),
    },
    iconText: {},
    icon: {
      fill: theme.palette.primary.main,
      borderWidth: 0,
      borderRadius: 24,
      '&$activeIcon': {
        fill: 'theme.palette.primary.main',
      },
    },
    activeIcon: {},
    stepperContainer: {
      padding: theme.spacing(3, 0),
    },
    label: {
      fontSize: theme.typography.pxToRem(14),
      marginBottom: theme.spacing(5),
      fontWeight: theme.typography.fontWeightRegular,
    },
    gridPadding: {
      marginTop: '0px',
      marginBottom: '-36px',
    },
    fieldLabel: {
      marginTop: theme.spacing(5),
      fontWeight: theme.typography.fontWeightMedium,
      fontSize: theme.typography.pxToRem(20),
      color: theme.palette.common.black,
      marginBottom: theme.spacing(5),
      marginLeft: theme.spacing(5),
      marginRight: theme.spacing(5),
    },
    stepLabel: {
      fontWeight: theme.typography.fontWeightRegular,
    },
    contentRoot: {
      borderColor: theme.palette.common.white,
      paddingTop: theme.spacing(2),
    },
    uploadButton: {
      backgroundColor: theme.palette.primary.main,
      minWidth: theme.spacing(25),
      width: theme.spacing(50),
      color: theme.palette.common.white,
      '&:disabled': {
        color: theme.palette.common.white,
      },
      marginTop: theme.spacing(2),
    },
    seperator: {
      textAlign: 'center',
      paddingLeft: theme.spacing(7),
    },
    divider: {
      margin: '10px auto',
      height: '50%',
    },
    multiProductTemplateIcon: {
      color: theme.palette.primary.main,
      marginTop: theme.spacing(-11),
    },
    multiProductTemplateText: {
      fontSize: theme.typography.pxToRem(14),
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.primary.main,
      marginLeft: theme.spacing(1),
      marginTop: theme.spacing(-11),
    },
    multiProductTemplateButton: {
      padding: 2,
      borderRadius: 0,
      '&:hover': {
        background: 'none',
        textDecoration: 'underline',
      },
    },
    resetConnector: {
      display: 'none',
    },
    uploadDisable: {
      backgroundColor: theme.palette.grey[500],
    },
  }
})
interface OligosInput {
  name: string
  sequence?: string
  customerOverhang?: string
  antisenceName: string
  antisenceSequence: string
  customerAntisenseOverHang?: string
  amount: string
  comments: string
}

interface DuplexInput {
  name: string
  sequence?: string
  fiveMod: string
  customerOverhang?: string
  threeMod: string
  antisenceName: string
  antisenceFiveMod?: string
  antisenceSequence: string
  antisenceThreeMod: string
  customerAntisenseOverHang?: string
  amount: string
  purification: string
  format?: string
  tubes: string
  comments: string
}
let startRow: any = 0
const parseSpreadsheet = async (
  file?: File,
  productType?: string
): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!file) {
      return resolve('')
    }
    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const XLSX = await import('xlsx')
        const data = new Uint8Array((e.target as any).result)
        const workbook = XLSX.read(data, { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[sheetName]
        const hasHeaders = true
        let products: OligosInput[] = []
        let mergedProducts = ''
        const buildFastDuplexProduct = (oligo) => {
          let {
            name,
            sequence,
            customerOverhang,
            antisenceName,
            antisenceSequence,
            customerAntisenseOverHang,
            amount,
            comments,
          } = oligo

          name = name ? name.toString().trim() : ''
          sequence = sequence ? sequence.toString().trim() : ''
          customerOverhang = customerOverhang
            ? customerOverhang.toString().trim()
            : ''
          antisenceName = antisenceName ? antisenceName.toString().trim() : ''
          antisenceSequence = antisenceSequence
            ? antisenceSequence.toString().trim()
            : ''
          customerAntisenseOverHang = customerAntisenseOverHang
            ? customerAntisenseOverHang.toString().trim()
            : ''
          amount = amount ? amount.toString().trim() : ''
          comments = comments ? comments.toString().trim() : ''

          return {
            name,
            sequence,
            customerOverhang,
            antisenceName,
            antisenceSequence,
            customerAntisenseOverHang,
            amount,
            comments,
          }
        }
        const buildmiRNAProduct = (oligo) => {
          let {
            antisenceName,
            antisenceSequence,
            antisenceThreeMod,
            name,
            fiveMod,
            threeMod,
            amount,
            purification,
            format,
            tubes,
            comments,
          } = oligo

          antisenceName = antisenceName ? antisenceName.toString().trim() : ''
          antisenceSequence = antisenceSequence
            ? antisenceSequence.toString().trim()
            : ''
          antisenceThreeMod = antisenceThreeMod
            ? antisenceThreeMod.toString().trim()
            : ''
          name = name ? name.toString().trim() : ''
          fiveMod = fiveMod ? fiveMod.toString().trim() : ''
          threeMod = threeMod ? threeMod.toString().trim() : ''
          amount = amount ? amount.toString().trim() : ''
          purification = purification ? purification.toString().trim() : ''
          format = format ? format.toString().trim() : ''
          tubes = tubes ? tubes.toString().trim() : ''
          comments = comments ? comments.toString().trim() : ''
          return {
            antisenceName,
            antisenceSequence,
            antisenceThreeMod,
            name,
            fiveMod,
            threeMod,
            amount,
            purification,
            format,
            tubes,
            comments,
          }
        }
        const buildDuplexProduct = (oligo) => {
          let {
            name,
            fiveMod,
            sequence,
            threeMod,
            customerOverhang,
            antisenceName,
            antisenceFiveMod,
            antisenceSequence,
            antisenceThreeMod,
            customerAntisenseOverHang,
            amount,
            purification,
            format,
            tubes,
            comments,
          } = oligo

          name = name ? name.toString().trim() : ''
          fiveMod = fiveMod ? fiveMod.toString().trim() : ''
          sequence = sequence ? sequence.toString().trim() : ''
          threeMod = threeMod ? threeMod.toString().trim() : ''
          customerOverhang = customerOverhang
            ? customerOverhang.toString().trim()
            : ''
          antisenceName = antisenceName ? antisenceName.toString().trim() : ''
          antisenceFiveMod = antisenceFiveMod
            ? antisenceFiveMod.toString().trim()
            : ''
          antisenceSequence = antisenceSequence
            ? antisenceSequence.toString().trim()
            : ''
          antisenceThreeMod = antisenceThreeMod
            ? antisenceThreeMod.toString().trim()
            : ''
          customerAntisenseOverHang = customerAntisenseOverHang
            ? customerAntisenseOverHang.toString().trim()
            : ''
          amount = amount ? amount.toString().trim() : ''
          purification = purification ? purification.toString().trim() : ''
          format = format ? format.toString().trim() : ''
          tubes = tubes ? tubes.toString().trim() : ''
          comments = comments ? comments.toString().trim() : ''
          return {
            name,
            fiveMod,
            sequence,
            threeMod,
            customerOverhang,
            antisenceName,
            antisenceFiveMod,
            antisenceSequence,
            antisenceThreeMod,
            customerAntisenseOverHang,
            amount,
            purification,
            format,
            tubes,
            comments,
          }
        }
        if (productType === BiTubeProduct.FastDuplex) {
          // for fastduplex
          if (sheetName === FileFormat.FastDuplexSheetName) {
            startRow = 11
            worksheet['!ref'] = 'A1:K300'
            products = (
              XLSX.utils.sheet_to_json(worksheet, {
                header: [
                  'name',
                  'sequence',
                  'customerOverhang',
                  'antisenceName',
                  'antisenceSequence',
                  'customerAntisenseOverHang',
                  'amount',
                  'comments',
                ],
                blankrows: true,
                defval: '',
              }) as OligosInput[]
            ).map(buildFastDuplexProduct)

            if (hasHeaders) {
              products.splice(0, 10)
              checkMultipleLine(products, resolve)
            }
          } else {
            resolve(FileFormat.InvalidTemplate)
          }
        } else if (productType === BiTubeProduct.mirna) {
          // for mirna
          if (sheetName === FileFormat.miRNADuplex) {
            startRow = 8

            worksheet['!ref'] = 'A1:K300'
            products = (
              XLSX.utils.sheet_to_json(worksheet, {
                header: [
                  'antisenceName',
                  'antisenceSequence',
                  'antisenceThreeMod',
                  'name',
                  'fiveMod',
                  'threeMod',
                  'amount',
                  'purification',
                  'format',
                  'tubes',
                  'comments',
                ],
                blankrows: true,
                defval: '',
              }) as DuplexInput[]
            ).map(buildmiRNAProduct)
            if (hasHeaders) {
              products.splice(0, 7)
              checkMultipleLine(products, resolve)
            }
          } else {
            resolve(FileFormat.InvalidTemplate)
          }
        } else {
          //for duplex
          if (sheetName === FileFormat.DuplexSheetName) {
            startRow = 7
            worksheet['!ref'] = 'A1:O300'
            products = (
              XLSX.utils.sheet_to_json(worksheet, {
                header: [
                  'name',
                  'fiveMod',
                  'sequence',
                  'threeMod',
                  'customerOverhang',
                  'antisenceName',
                  'antisenceFiveMod',
                  'antisenceSequence',
                  'antisenceThreeMod',
                  'customerAntisenseOverHang',
                  'amount',
                  'purification',
                  'format',
                  'tubes',
                  'comments',
                ],
                blankrows: true,
                defval: '',
              }) as DuplexInput[]
            ).map(buildDuplexProduct)
            if (hasHeaders) {
              products.splice(0, 6)
              checkMultipleLine(products, resolve)
            }
          } else {
            resolve(FileFormat.InvalidTemplate)
          }
        }

        products.forEach((product, index) => {
          let val = ''
          for (const value in product) {
            value !== 'comments'
              ? (val += `${product[value]}\t`)
              : (val += `${product[value]}`)
          }
          index !== products.length - 1
            ? (mergedProducts += `${val}\n`)
            : (mergedProducts += `${val}`)
        })

        resolve(mergedProducts)
      } catch (err) {
        reject(err)
      }
    }
    reader.readAsArrayBuffer(file)
  })

const FastMultipleEntryInfo = (props) => {
  const classes = useStyles()
  const intl = useIntl()
  const [text, setText] = useState('')
  const [upload, setUpload] = useState(false)
  const [copyPasteTube, { loading: copypasteLoading }] =
    useCopyPasteTubeMutation()
  const [uploadTubesFromFile, { loading: fileUploadLoading }] =
    useUploadTubesFromFileMutation()
  const { productDetails, setItemDetails } = props
  const [validationMessage, setValidationMessage] = useState<string[]>([])
  const { bitubeState, biTubeDispatch } = React.useContext(biTubeConfigContext)
  let { sessionData, currentOligoIndex } = bitubeState
  const router = useRouter()
  const [disableUpload, setDisableUpload] = useState(true)
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [uploadDataType, setUploadDataType] = React.useState('')
  const [datavalue, setDatavalue] = React.useState<any>('')
  const reviewPath = `${fastDuplexOligosRoute.index()}?product=${
    productDetails.productType
  }&activeLink=${ActiveLinkType.ReviewPage}`
  const [sessionFields] = useSessionFieldsMutation()

  const setData = (obj) => {
    sessionData = [EMPTY_SESSION]
    obj.forEach((item: any, index: number) => {
      if (index > 0) {
        const emptyStateObj = Object.assign({}, EMPTY_SESSION)
        sessionData.push(emptyStateObj)
      }
      sessionData[index].comments = item.comments
      sessionData[index].additionalComments = item.additionalComments
      sessionData[index].name = item.name
      sessionData[index].applyToAll = item.applyToAll
      sessionData[index].id = item.id.toString()
      sessionData[index].oligoId = item.id
      sessionData[index].baseCount = item.baseCount
      sessionData[index].concentration = item.concentration
      sessionData[index].countryCode = item.countryCode
      sessionData[index].fiveMod = item.fiveMod
      sessionData[index].threeMod = item.threeMod
      sessionData[index].sequence = item.sequence
      sessionData[index].scale = item.scale
      sessionData[index].purification = item.purification
      sessionData[index].format = item.format
      sessionData[index].isOverridden = item.isOverridden
      sessionData[index].tubes = item.tubes.toString()
      sessionData[index].listError = item.listError
      sessionData[index].price = item.price
      sessionData[index].currencySymbol = item.currencySymbol
      sessionData[index].currency = item.currency
      sessionData[index].antisenceName = item.antisenceName
      sessionData[index].antisenceBaseCount = item.antisenceBaseCount
      sessionData[index].antisenceSequence = item.antisenceSequence
      sessionData[index].customerAntisenseOverHang =
        item.customerAntisenseOverHang
      sessionData[index].customerOverhang = item.customerOverhang
      sessionData[index].amount = item.amount
      sessionData[index].antisenceFiveMod = item.antisenceFiveMod
      sessionData[index].antisenceThreeMod = item.antisenceThreeMod
      sessionData[index].autoGenerateOligo = item.autoGenerateOligo
        ? item.autoGenerateOligo
        : false
      sessionData[index].senseNameCheck = item.senseNameCheck
        ? item.senseNameCheck
        : false
    })

    const newIndex = sessionData.length - 1
    biTubeDispatch({
      type: 'UPDATE_OLIGO_INDEX',
      data: newIndex,
    })
    biTubeDispatch({
      type: 'UPDATE_CURRENT_OLIGO_INDEX',
      data: newIndex,
    })

    biTubeDispatch({
      type: 'ADD_NEW_OLIGO',
      data: sessionData.length - 1,
    })

    biTubeDispatch({
      type: 'UPDATE_INPUT',
      data: [...sessionData],
    })
    const data = sessionData[newIndex]

    biTubeDispatch({
      type: 'UPDATE_ERROR_DATA',
      data: {
        ...bitubeState.errorData,
        threeMod: data.threeMod,
        customerOverhang: data.customerOverhang,
        fiveMod: data.fiveMod,
        name: data.name,
        sequence: data.sequence,
        antisenceName: data.antisenceName,
        antisenceSequence: data.antisenceSequence,
        customerAntisenseOverHang: data.customerAntisenseOverHang,
      },
    })
  }
  const performCopyPaste = (key, value) => {
    setValidationMessage([])

    if (key && key !== '') {
      if (key === messages.CONF_SUCCESS.defaultMessage && value) {
        const obj: any = JSON.parse(value)
        setData(obj)
        setItemDetails(obj)
        biTubeDispatch({ type: 'IS_CONFIRM_ORDER', data: true })
        biTubeDispatch({ type: 'SET_STEP_NO', data: 1 })
        biTubeDispatch({
          type: 'SET_CONTINUE_TO_NEXT_PAGE',
          data: false,
        })
        props.navigate()
      } else if (key === messages.CONF_REVIEW_ERROR.defaultMessage && value) {
        // redirect to review page
        const obj: any = JSON.parse(value)
        setData(obj)
        biTubeDispatch({ type: 'SET_STEP_NO', data: 0 })
        biTubeDispatch({ type: 'IS_CONFIRM_ORDER', data: false })
        biTubeDispatch({ type: 'IS_BULK_REVIEW', data: true })
        router.push(reviewPath)
      } else {
        biTubeDispatch({ type: 'IS_PAGE_RELOAD', data: true })
        biTubeDispatch({
          type: 'SET_PAGE_ERROR',
          data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
        })
        setValidationMessage([key])
        setText('')
        window.scrollTo(0, 0)
      }
    }
    biTubeDispatch({ type: 'SET_ERROR_ICON_ON_PAGELOAD', data: false })
  }
  const copyPaste = async (obj) => {
    const response = await copyPasteTube({
      variables: {
        input: obj,
        productInput: productDetails,
        isOverride: false,
        isDForm: false,
      },
    })
    const { data } = response
    if (data && data.copyPasteTube) {
      if (!data.copyPasteTube.serverError) {
        const { key, value } = data.copyPasteTube
        performCopyPaste(key, value)
      } else {
        window.scrollTo(0, 0)
        biTubeDispatch({
          type: 'SET_PAGE_ERROR',
          data: ConfiguratorValues.InternalServerError,
        })
      }
    }
  }

  const handleText = (e) => {
    if (e.target.value.length !== 0) {
      setDisableUpload(false)
    } else {
      setDisableUpload(true)
    }
    biTubeDispatch({ type: 'SET_PAGE_ERROR', data: '' })
    setValidationMessage([])
    setText(e.target.value.replaceAll('Click to Choose', ''))
  }

  const ValidateCopyPaste = () => {
    setUpload(false)
    if (
      sessionData.length === 1 &&
      sessionData[0].name === '' &&
      sessionData[0].sequence === '' &&
      sessionData[0].antisenceName === '' &&
      sessionData[0].antisenceSequence === ''
    ) {
      copyPaste(convertToASCII(text))
    } else {
      setUploadDataType(UploadType.copyPaste)
      setDatavalue(text)
      setDialogOpen(true)
    }
  }

  const validateText = () => {
    if (text !== null) {
      ValidateCopyPaste()
    }
  }
  const handleUploadChange = async (file) => {
    biTubeDispatch({ type: 'SET_PAGE_ERROR', data: '' })
    setUpload(true)
    setValidationMessage([])
    if (
      FileFormat.acceptFormat &&
      FileFormat.acceptFormat
        .split(',')
        .map((type) => type.trim().toLowerCase())
        .includes(file.type)
    ) {
      const inputStr = await parseSpreadsheet(file, productDetails.productType)
      //make api call here passing inputStr as input
      if (inputStr === FileFormat.InvalidTemplate) {
        biTubeDispatch({ type: 'IS_PAGE_RELOAD', data: true })
        biTubeDispatch({
          type: 'SET_PAGE_ERROR',
          data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
        })
        setValidationMessage([
          intl.formatMessage(messages.CONF_INVALID_TEMPLATE),
        ])
        window.scrollTo(0, 0)
      }
      if (inputStr === FileFormat.MultipleLineError) {
        biTubeDispatch({ type: 'IS_PAGE_RELOAD', data: true })
        biTubeDispatch({
          type: 'SET_PAGE_ERROR',
          data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
        })
        setValidationMessage([intl.formatMessage(messages.MULTIPLE_LINE_ERROR)])
        window.scrollTo(0, 0)
      }
      if (inputStr === '') {
        biTubeDispatch({ type: 'IS_PAGE_RELOAD', data: true })
        biTubeDispatch({
          type: 'SET_PAGE_ERROR',
          data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
        })
        setValidationMessage([intl.formatMessage(messages.CONF_EMPTY_FILE)])
        window.scrollTo(0, 0)
      }
      if (
        inputStr !== '' &&
        inputStr !== FileFormat.MultipleLineError &&
        inputStr !== FileFormat.InvalidTemplate
      ) {
        biTubeDispatch({ type: 'SET_PAGE_ERROR', data: '' })
        const response = await uploadTubesFromFile({
          fetchPolicy: 'no-cache',
          variables: {
            input: inputStr,
            productInput: productDetails,
            isOverride: false,
            isDForm: false,
            startRow: startRow,
          },
        })
        const { data } = response

        if (data && data.uploadTubesFromFile) {
          if (!data.uploadTubesFromFile.serverError) {
            const { key, value } = data.uploadTubesFromFile
            performCopyPaste(key, value)
          } else {
            window.scrollTo(0, 0)
            biTubeDispatch({
              type: 'SET_PAGE_ERROR',
              data: ConfiguratorValues.InternalServerError,
            })
          }
        }
      }
    } else {
      biTubeDispatch({
        type: 'SET_PAGE_ERROR',
        data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
      })
      setValidationMessage([FileFormat.invalidFormatMsg])
      window.scrollTo(0, 0)
    }
  }
  const validateUploadFile = (file) => {
    setText('')
    setDisableUpload(true)
    if (
      sessionData.length === 1 &&
      sessionData[0].name === '' &&
      sessionData[0].sequence === '' &&
      sessionData[0].antisenceName === '' &&
      sessionData[0].antisenceSequence === ''
    ) {
      handleUploadChange(file)
    } else {
      setUploadDataType(UploadType.fileUpload)
      setDatavalue(file)
      setDialogOpen(true)
    }
  }

  const sessionDataToSession = async (obj) => {
    await sessionFields({
      variables: { input: obj, productInput: productDetails },
    })
  }
  const clearSessionData = () => {
    biTubeDispatch({
      type: 'UPDATE_OLIGO_INDEX',
      data: 0,
    })
    biTubeDispatch({
      type: 'UPDATE_CURRENT_OLIGO_INDEX',
      data: 0,
    })
    const sessionArray = [
      {
        isOverridden: false,
        name: '',
        sequence: '',
        fiveMod: '',
        purification: '',
        baseCount: 0,
        format: '',
        concentration: '',
        tubes: '1',
        price: '',
        scale: '',
        orderItemId: '',
        id: '1',
        comments: '',
        additionalComments: '',
        oligoId: 0,
        countryCode: '',
        autoValidation: 'false',
        productType: '',
        customerOverhang: '',
        antisenceName: '',
        antisenceSequence: '',
        autoGenerateOligo: false,
        customerAntisenseOverHang: '',
        antisenceBaseCount: 0,
        amount: '',
        senseNameCheck: false,
        listError: [{ key: '', value: '' }],
        currencySymbol: '',
        currency: '',
        antisenceThreeMod: '',
        antisenceFiveMod: '',
        threeMod: '',
      },
    ]
    bitubeState.sessionData = sessionArray
    biTubeDispatch({
      type: 'UPDATE_INPUT',
      data: sessionArray,
    })
    sessionDataToSession(sessionArray)
    biTubeDispatch({
      type: 'UPDATE_PAGE_LOAD',
      data: 1,
    })
    biTubeDispatch({ type: 'UPDATE_SEQUENCE_LENGTH', data: 0 })
    biTubeDispatch({ type: 'IS_PAGE_RELOAD', data: true })
    biTubeDispatch({ type: 'IS_CONFIRM_ORDER', data: false })
    biTubeDispatch({ type: 'CLEAR_SESSION_DATA', data: true })
    biTubeDispatch({ type: 'SET_STEP_NO', data: 0 })
    biTubeDispatch({
      type: 'UPDATE_ERROR_DATA',
      data: {
        ...bitubeState.errorData,
        name: '',
        sequence: '',
        scale: '',
        purification: '',
        fiveMod: '',
        threeMod: '',
        researcher: '',
        format: '',
        concentration: '',
        tubes: '1',
        countryCode: '',
        customerOverhang: '',
        antisenceName: '',
        antisenceSequence: '',
        isOverridden: false,
        customerAntisenseOverHang: '',
        antisenceBaseCount: 0,
        amount: '',
        antisenceFiveMod: '',
        antisenceThreeMod: '',
      },
    })
  }

  const saveToSession = (type, data) => {
    clearSessionData()
    if (type === UploadType.copyPaste) {
      copyPaste(convertToASCII(data))
    } else if (type === UploadType.fileUpload) {
      handleUploadChange(data)
    }
  }
  useEffect(() => {
    return function cleanupError() {
      biTubeDispatch({ type: 'SET_PAGE_ERROR', data: '' })
      if (
        sessionData[currentOligoIndex].name === '' &&
        sessionData[currentOligoIndex].sequence === '' &&
        sessionData[currentOligoIndex].antisenceName === '' &&
        sessionData[currentOligoIndex].antisenceSequence === ''
      ) {
        biTubeDispatch({ type: 'IS_VALID_TOTAL_VALIDATION', data: true })
        biTubeDispatch({ type: 'SET_ERROR_ON_PAGE_REFRESH', data: false })
        biTubeDispatch({ type: 'OVERRIDDEN_NUMBER', data: 0 })
        biTubeDispatch({ type: 'SPECIFICATION_TOTAL_VALIDATION', data: false })
        sessionData[currentOligoIndex].listError = []
      } else {
        biTubeDispatch({ type: 'SET_ERROR_ICON_ON_PAGELOAD', data: false })
        biTubeDispatch({ type: 'IS_VALID_TOTAL_VALIDATION', data: false })
        biTubeDispatch({ type: 'SET_ERROR_ON_PAGE_REFRESH', data: true })
        biTubeDispatch({ type: 'OVERRIDDEN_NUMBER', data: 0 })
        if (
          sessionData[currentOligoIndex].amount === '' &&
          sessionData[currentOligoIndex].purification === '' &&
          sessionData[currentOligoIndex].format === ''
        ) {
          biTubeDispatch({ type: 'SPECIFICATION_TOTAL_VALIDATION', data: true })
        } else {
          biTubeDispatch({
            type: 'SPECIFICATION_TOTAL_VALIDATION',
            data: false,
          })
        }
      }
    }
    // TODO: need a test before we refactor these dependencies
  }, [])
  return (
    <div className={classes.contentWrapper}>
      <div>
        <FormattedMessage
          {...messages.CONF_OPTIONAL_INTR_MOD_TEMPLATE_standard}
        />
      </div>

      <Stepper
        orientation="vertical"
        classes={{ root: classes.stepperContainer }}
        connector={
          <StepConnector
            classes={{
              root: classes.resetConnector,
            }}
          />
        }
        nonLinear
      >
        <Step active className={classes.step}>
          <StepLabel
            StepIconProps={{
              classes: {
                root: classes.icon,
                active: classes.activeIcon,
                text: classes.iconText,
              },
            }}
          >
            <div>
              <Grid
                container
                alignItems="center"
                spacing={5}
                className={classes.gridPadding}
              >
                <Grid item sm={5}>
                  <div className={classes.label}>
                    <FormattedMessage
                      {...messages.CONF_MULTIPLE_ENTRY_STEP_1}
                    />
                  </div>
                  <br />
                </Grid>
                <Grid item sm={5}>
                  <IconButton
                    className={classes.multiProductTemplateButton}
                    href={`/configurator-upload/bulk-entry/template/${productDetails.productType}/${productDetails.countryCode}?isCSR=${productDetails.isCSR}&isCSRSupervisor=${productDetails.isCSRSupervisor}`}
                    onClick={() =>
                      sendConfiguratorGADownloadEvent(
                        EventActions.Templates,
                        `${productDetails.productType}`
                      )
                    }
                  >
                    <CloudDownloadIcon
                      className={classes.multiProductTemplateIcon}
                    />
                    <div
                      className={classes.multiProductTemplateText}
                      data-testid="configurator_download-file-template"
                    >
                      {messages.CONF_MULTIPLE_ENTRY_TEMPLATE.defaultMessage}
                    </div>
                  </IconButton>
                </Grid>
              </Grid>
            </div>
          </StepLabel>
        </Step>
        <Step active className={clsx(classes.step, classes.mTop12)}>
          <StepLabel
            StepIconProps={{
              classes: {
                root: classes.icon,
                active: classes.activeIcon,
                text: classes.iconText,
              },
            }}
          >
            <div className={classes.stepLabel}>
              <FormattedMessage {...messages.CONF_MULTIPLE_ENTRY_STEP_2} />
            </div>
          </StepLabel>
        </Step>
        <Step active className={classes.step}>
          <StepLabel
            StepIconProps={{
              classes: {
                root: classes.icon,
                active: classes.activeIcon,
                text: classes.iconText,
              },
            }}
          >
            <div className={classes.stepLabel}>
              <FormattedMessage {...messages.CONF_MULTIPLE_ENTRY_STEP_3} />
            </div>
          </StepLabel>
          <StepContent
            classes={{
              root: classes.contentRoot,
            }}
          >
            <Grid container>
              <Grid item sm={3}>
                <UploadTemplate
                  accept={FileFormat.acceptFormat}
                  handleUploadChange={([file]) => validateUploadFile(file)}
                />
                {upload && (
                  <div>
                    <ValidationErrors errors={validationMessage} />
                  </div>
                )}
              </Grid>
              <Grid item className={classes.seperator}>
                <span className={classes.fieldLabel}>
                  <FormattedMessage {...messages.CONF_OR} />
                </span>
                <Divider orientation="vertical" className={classes.divider} />
              </Grid>
              <Grid item sm={7}>
                <CopyEntry
                  handlechange={handleText}
                  text={text}
                  productDetails={productDetails}
                />
                {!upload && (
                  <div>
                    <ValidationErrors errors={validationMessage} />
                  </div>
                )}
              </Grid>
            </Grid>
          </StepContent>
        </Step>
        <Step active className={classes.step}>
          <StepLabel
            StepIconProps={{
              classes: {
                root: classes.icon,
                active: classes.activeIcon,
                text: classes.iconText,
              },
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="large"
              className={clsx(
                classes.uploadButton,
                (fileUploadLoading || disableUpload) && classes.uploadDisable
              )}
              type="submit"
              onClick={validateText}
              disabled={fileUploadLoading || disableUpload}
              id="duplex-upload-button"
              data-testid="duplex-upload-button"
            >
              <FormattedMessage {...messages.CONF_UPLOAD_YOUR_DATA} />
            </Button>
          </StepLabel>
        </Step>
      </Stepper>
      <CautionPopUp
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        saveToSession={saveToSession}
        uploadDataType={uploadDataType}
        data={datavalue}
        prodType={UploadType.prodType}
      />
      {(copypasteLoading || fileUploadLoading) && <CircleLoader />}
    </div>
  )
}

export default FastMultipleEntryInfo
